/* Copyright (c) 2026 Health Endeavors, LLC. All Rights Reserved */

import { Global } from '@emotion/react';
import { styled } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import Backdrop from '@mui/material/Backdrop';
import { PatientSummary } from '../PatientSummary/PatientSummary';
import { useAppDispatch, useAppSelector } from '../../../../../app/hooks';
import {
  clearSummary,
  resetThreePaneViewStates,
  selectPatientSummaryState,
} from '../PatientSummary/patientSummarySlice';
import { useCallback, useEffect } from 'react';

const CustomBackdrop = styled(Backdrop)(() => ({
  backgroundColor: 'transparent',
}));

const Root = styled('div')(() => ({
  height: '100%',
  backgroundColor: 'transparent',
}));

const StyledBox = styled('div')(() => ({
  backgroundColor: 'var(--blue70)',
  height: '-webkit-fill-available',
}));

export default function SummaryDrawer({ open, setOpen }: { open: boolean; setOpen: (open: boolean) => void }) {
  const { summary } = useAppSelector(selectPatientSummaryState);

  const dispatch = useAppDispatch();
  const container = document.getElementById('summary-drawer-container') || undefined;
  const topOpen = document.getElementById('summary-button')?.getBoundingClientRect().top || 105;
  const topClosed = document.getElementById('view-summary')?.getBoundingClientRect().top || 105;

  const toggleDrawer = (newOpen: boolean) => () => {
    setOpen(newOpen);
  };

  const resetSummary = useCallback(() => {
    dispatch(clearSummary());
    dispatch(resetThreePaneViewStates());
  }, [dispatch]);

  useEffect(() => {
    // resetSummary on component unmounting
    return resetSummary;
  }, [resetSummary]);

  useEffect(() => {
    summary && setOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [summary]);

  if (!summary) {
    return null;
  }

  return (
    <Root>
      <CssBaseline />
      <Global
        styles={{
          '.MuiDrawer-root > .MuiPaper-root': {
            overflow: 'visible',
          },
        }}
      />
      <Drawer container={container} anchor="right" open={open} keepMounted BackdropComponent={CustomBackdrop}>
        <StyledBox
          sx={{
            position: 'absolute',
            left: open ? -438 : -18,
            visibility: 'visible',
            top: open ? topOpen : topClosed,
            width: '438px',
          }}
        >
          {open ? (
            <Box sx={{ p: '10px', mr: '20px' }}>
              <Button variant="text" sx={{ mb: '25px', mt: '15px' }} onClick={toggleDrawer(false)}>
                Hide Summary
              </Button>
              <PatientSummary />
            </Box>
          ) : null}
        </StyledBox>
      </Drawer>
    </Root>
  );
}
